import { useContext, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { LogoColor, LogoGray, LogoWhite } from '../../../Assets';
import { camelize } from '../../../Utils';

import './styles.scss';
import LogoApp from '../../../Components/logo-app/LogoApp';
import { useUserStore } from '../../../stores/user';
import AppConfigContext, {
	BrandingType,
} from '../../../Components/AppConfig/AppConfigContext';
import getBrandingBackground from '../../../Utils/getBrandingBackground';

/**
 * Mapeo de rutas hijas del ExternalLayout → llave del manifest.branding que
 * provee la imagen del lateral derecho. Si la ruta no calza, cae al default
 * de SCSS (/assets/ExternalLayout_Background.png).
 */
function brandingKeyForPath(pathname: string): keyof BrandingType | null {
	if (pathname.startsWith('/register')) return 'background_signup';
	if (pathname.startsWith('/forgot-password')) return 'background_recover_pswd';
	if (pathname.startsWith('/update-password')) {
		return 'background_change_recover_pswd';
	}
	return null;
}

export interface ExternalLayoutOutletContext {
	title: string;
	setTitle: (title: string) => void;
}

export default function ExternalLayout() {
	const [title, setTitle] = useState<string>('');
	const outletContext: ExternalLayoutOutletContext = { title, setTitle };
	const isLogged = useUserStore((state) => state.isLogged);
	const navigate = useNavigate();
	const location = useLocation();
	const { manifest } = useContext(AppConfigContext);

	if (isLogged) {
		return <Navigate to="/courses" replace />;
	}

	// Imagen del lado derecho según ruta. `getBrandingBackground` devuelve
	// `{}` si no hay URL ni fallback — en ese caso el SCSS provee el default.
	const brandingKey = brandingKeyForPath(location.pathname);
	const imageStyle = brandingKey
		? getBrandingBackground(manifest, brandingKey)
		: {};

	return (
		<div className={`ExternalLayout ${camelize(title)}`}>
			<Container fluid className="ExternalLayout_Wrapper">
				<Row className="h-100 h-auto-sx px-0">
					<Col
						lg={6}
						className="ExternalLayout_Wrapper_Outlet order-2 order-lg-1"
					>
						<header
							className="shadow-sm d-lg-block"
							onClick={() => {
								navigate('/');
							}}
						>
							<LogoApp />
						</header>
						<section>
							<Outlet context={outletContext} />
						</section>
						<footer className="border-top">
							<LogoColor className="d-lg-block" />
							<LogoGray className="d-lg-none" />
						</footer>
					</Col>
					<Col
						lg={6}
						className="ExternalLayout_Wrapper_Image order-1 order-lg-2"
						style={imageStyle}
					>
						{title !== '' && (
							<div className="h-100 d-flex justify-content-center align-items-center position-relative pt-5 pb-5">
								<div className="d-lg-none z-index-2 text-center position-relative">
									<LogoWhite className="mb-4" />
									<h1>{title}</h1>
								</div>
								<div className="bg-black h-100 w-100 opacity-50 position-absolute z-index-1 d-lg-none" />
							</div>
						)}
					</Col>
				</Row>
			</Container>
		</div>
	);
}
